import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, User, UsersThree, PencilSimple } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../config';

const TEAM_COLORS = [
  '#00CBA9', // VMK Teal
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#ec4899', // Pink
  '#10b981', // Emerald
  '#6366f1', // Indigo
];
const PAGE_SIZE = 6;

const TeamsPage = () => {
  const { t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showEditTeamDialog, setShowEditTeamDialog] = useState(false);
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [showEditWorkerDialog, setShowEditWorkerDialog] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', type: 'individual', members: [], color: TEAM_COLORS[0] });
  const [newWorker, setNewWorker] = useState({ name: '', phone: '', email: '' });
  const [editTeam, setEditTeam] = useState(null);
  const [editWorker, setEditWorker] = useState(null);
  const [teamsPage, setTeamsPage] = useState(1);
  const [workersPage, setWorkersPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, workersRes] = await Promise.all([
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/workers`, { withCredentials: true })
      ]);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
      setWorkers(Array.isArray(workersRes.data) ? workersRes.data : []);
    } catch (error) {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error(t('teamNameRequired'));
      return;
    }

    try {
      await axios.post(`${API}/teams`, newTeam, { withCredentials: true });
      toast.success(t('teamCreatedSuccess'));
      setShowTeamDialog(false);
      setNewTeam({ name: '', type: 'individual', members: [], color: TEAM_COLORS[0] });
      fetchData();
    } catch (error) {
      toast.error(t('teamCreateFailed'));
    }
  };

  const handleOpenEditTeam = (team) => {
    setEditTeam({
      id: team.id,
      name: team.name || '',
      type: team.type || 'individual',
      members: Array.isArray(team.members) ? team.members : [],
      color: team.color || TEAM_COLORS[0],
    });
    setShowEditTeamDialog(true);
  };

  const handleSaveEditTeam = async () => {
    if (!editTeam || !editTeam.name.trim()) {
      toast.error(t('teamNameRequired'));
      return;
    }
    const payload = {
      name: editTeam.name.trim(),
      type: editTeam.type,
      color: editTeam.color,
      members: Array.isArray(editTeam.members) ? editTeam.members : [],
    };
    try {
      await axios.put(`${API}/teams/${editTeam.id}`, payload, { withCredentials: true });
      toast.success(t('teamUpdatedSuccess'));
      setShowEditTeamDialog(false);
      setEditTeam(null);
      fetchData();
    } catch {
      toast.error(t('teamUpdateFailed'));
    }
  };

  const handleCreateWorker = async () => {
    if (!newWorker.name.trim()) {
      toast.error(t('workerNameRequired'));
      return;
    }

    try {
      await axios.post(`${API}/workers`, newWorker, { withCredentials: true });
      toast.success(t('workerAddedSuccess'));
      setShowWorkerDialog(false);
      setNewWorker({ name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(t('workerAddFailed'));
    }
  };

  const handleOpenEditWorker = (worker) => {
    setEditWorker({
      id: worker.id,
      name: worker.name || '',
      phone: worker.phone || '',
      email: worker.email || '',
    });
    setShowEditWorkerDialog(true);
  };

  const handleSaveEditWorker = async () => {
    if (!editWorker || !editWorker.name.trim()) {
      toast.error(t('workerNameRequired'));
      return;
    }
    try {
      await axios.put(
        `${API}/workers/${editWorker.id}`,
        {
          name: editWorker.name.trim(),
          phone: editWorker.phone || '',
          email: editWorker.email || '',
        },
        { withCredentials: true }
      );
      toast.success(t('workerUpdatedSuccess'));
      setShowEditWorkerDialog(false);
      setEditWorker(null);
      fetchData();
    } catch {
      toast.error(t('workerUpdateFailed'));
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm(t('confirmDeleteTeam'))) return;

    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      toast.success(t('teamDeletedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('teamDeleteFailed'));
    }
  };

  const handleDeleteWorker = async (workerId) => {
    if (!window.confirm(t('confirmDeleteWorker'))) return;

    try {
      await axios.delete(`${API}/workers/${workerId}`, { withCredentials: true });
      toast.success(t('workerDeletedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('workerDeleteFailed'));
    }
  };

  const getWorkerNames = (memberIds) => {
    if (!Array.isArray(memberIds)) return '';
    return memberIds
      .map((id) => workers.find((w) => String(w.id) === String(id))?.name)
      .filter(Boolean)
      .join(', ');
  };

  const teamsSorted = useMemo(() => {
    const list = Array.isArray(teams) ? [...teams] : [];
    list.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' }));
    return list;
  }, [teams]);

  const workersSorted = useMemo(() => {
    const list = Array.isArray(workers) ? [...workers] : [];
    list.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' }));
    return list;
  }, [workers]);

  const totalTeamPages = Math.max(1, Math.ceil(teamsSorted.length / PAGE_SIZE));
  const totalWorkerPages = Math.max(1, Math.ceil(workersSorted.length / PAGE_SIZE));
  const paginatedTeams = teamsSorted.slice((teamsPage - 1) * PAGE_SIZE, teamsPage * PAGE_SIZE);
  const paginatedWorkers = workersSorted.slice((workersPage - 1) * PAGE_SIZE, workersPage * PAGE_SIZE);

  useEffect(() => {
    setTeamsPage((prev) => Math.min(prev, totalTeamPages));
  }, [totalTeamPages]);

  useEffect(() => {
    setWorkersPage((prev) => Math.min(prev, totalWorkerPages));
  }, [totalWorkerPages]);

  const renderPagination = (currentPage, totalPages, onPageChange, testIdPrefix) => {
    if (totalPages <= 1 || !Number.isFinite(totalPages)) return null;
    const safeTotal = Math.max(1, totalPages);
    const safeCurrent = Math.min(Math.max(1, currentPage), safeTotal);
    return (
      <div className="mt-4 flex items-center justify-between gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3 py-2 dark:border-[#334155] dark:bg-[#1e293b]">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safeCurrent - 1))}
          disabled={safeCurrent === 1}
          className="vmk-btn px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`${testIdPrefix}-prev`}
        >
          {t('prev')}
        </button>
        <span className="text-sm text-[#64748b] dark:text-[#94a3b8] tabular-nums">
          {t('page')} {safeCurrent} / {safeTotal}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(safeTotal, safeCurrent + 1))}
          disabled={safeCurrent === safeTotal}
          className="vmk-btn px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`${testIdPrefix}-next`}
        >
          {t('next')}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="teams-page" className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="page-title">{t('teamsAndWorkers')}</h1>
          <p className="page-subtitle">
            {t('manageTeams')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowWorkerDialog(true)}
            className="vmk-btn"
            data-testid="add-worker-btn"
          >
            <Plus size={18} />
            {t('addWorker')}
          </button>
          <button
            onClick={() => setShowTeamDialog(true)}
            className="vmk-btn vmk-btn-primary"
            data-testid="add-team-btn"
          >
            <Plus size={18} />
            {t('addTeam')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Teams Section */}
        <div>
          <h2 className="section-title flex items-center gap-2">
            <UsersThree size={16} />
            {t('teams')} ({teamsSorted.length})
          </h2>
          {teamsSorted.length === 0 ? (
            <div className="vmk-card text-center py-12">
              <div className="w-12 h-12 rounded-full bg-[#f1f5f9] dark:bg-[#1e293b] flex items-center justify-center mx-auto mb-3">
                <UsersThree size={24} className="text-[#94a3b8]" />
              </div>
              <p className="text-[#64748b] dark:text-[#94a3b8]">{t('noTeamsYet')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedTeams.map((team) => (
                <div
                  key={team.id}
                  className="vmk-card flex items-center justify-between"
                  data-testid={`team-card-${team.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center text-white"
                      style={{ backgroundColor: team.color }}
                    >
                      {team.type === 'group' ? (
                        <UsersThree size={22} weight="bold" />
                      ) : (
                        <User size={22} weight="bold" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1a1a1a] dark:text-[#e2e8f0]">{team.name}</h3>
                      <p className="text-sm text-[#64748b] dark:text-[#94a3b8]">
                        {team.type === 'group' ? t('teamGroup') : t('individualWorker')} •{' '}
                        {(Array.isArray(team.members) ? team.members.length : 0)}{' '}
                        {t('members').toLowerCase()}
                      </p>
                      {Array.isArray(team.members) && team.members.length > 0 && (
                        <p className="text-xs text-[#94a3b8] dark:text-[#cbd5e1] mt-1 break-words">
                          {getWorkerNames(team.members)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleOpenEditTeam(team)}
                      className="vmk-btn-ghost p-2 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-[#1e293b]"
                      data-testid={`edit-team-${team.id}`}
                      title={t('editOrder')}
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteTeam(team.id)}
                      className="vmk-btn-ghost p-2 rounded-lg text-[#ef4444] hover:bg-red-50 dark:hover:bg-red-950/40"
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
              {renderPagination(teamsPage, totalTeamPages, setTeamsPage, 'teams-pagination')}
            </div>
          )}
        </div>

        {/* Workers Section */}
        <div>
          <h2 className="section-title flex items-center gap-2">
            <User size={16} />
            {t('workers')} ({workersSorted.length})
          </h2>
          {workersSorted.length === 0 ? (
            <div className="vmk-card text-center py-12">
              <div className="w-12 h-12 rounded-full bg-[#f1f5f9] dark:bg-[#1e293b] flex items-center justify-center mx-auto mb-3">
                <User size={24} className="text-[#94a3b8]" />
              </div>
              <p className="text-[#64748b] dark:text-[#94a3b8]">{t('noWorkersYet')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedWorkers.map((worker) => (
                <div
                  key={worker.id}
                  className="vmk-card flex items-center justify-between"
                  data-testid={`worker-card-${worker.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#f1f5f9] dark:bg-[#1e293b] flex items-center justify-center">
                      <User size={22} className="text-[#64748b]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1a1a1a] dark:text-[#e2e8f0]">{worker.name}</h3>
                      <p className="text-sm text-[#64748b] dark:text-[#94a3b8] font-mono break-all">
                        {worker.phone || worker.email || t('noContactInfo')}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleOpenEditWorker(worker)}
                      className="vmk-btn-ghost p-2 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-[#1e293b]"
                      data-testid={`edit-worker-${worker.id}`}
                      title={t('editOrder')}
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteWorker(worker.id)}
                      className="vmk-btn-ghost p-2 rounded-lg text-[#ef4444] hover:bg-red-50 dark:hover:bg-red-950/40"
                      data-testid={`delete-worker-${worker.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
              {renderPagination(workersPage, totalWorkerPages, setWorkersPage, 'workers-pagination')}
            </div>
          )}
        </div>
      </div>

      {/* Add Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="vmk-dialog-content max-w-md dark:bg-[#0f172a] dark:border-[#334155]">
          <DialogHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-[#f8f9fb]">
            <DialogTitle className="text-lg font-bold">{t('addNewTeam')}</DialogTitle>
            <DialogDescription className="sr-only">{t('addNewTeam')}</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-5">
            <div>
              <label className="form-label mb-2 block">{t('teamName')}</label>
              <input
                type="text"
                value={newTeam.name}
                onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                className="vmk-input"
                placeholder={t('teamNamePlaceholder')}
                data-testid="team-name-input"
              />
            </div>

            <div>
              <label className="form-label mb-2 block">{t('type')}</label>
              <Select
                value={newTeam.type}
                onValueChange={(value) => setNewTeam(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="vmk-input h-12" data-testid="team-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">{t('individualWorker')}</SelectItem>
                  <SelectItem value="group">{t('teamGroup')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="form-label mb-3 block">{t('teamColor')}</label>
              <div className="flex gap-3 flex-wrap">
                {TEAM_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewTeam(prev => ({ ...prev, color }))}
                    className={`w-10 h-10 rounded-xl transition-all ${
                      newTeam.color === color 
                        ? 'ring-2 ring-offset-2 ring-[#1a1a1a] scale-110' 
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`team-color-${color}`}
                  />
                ))}
              </div>
            </div>

            {workers.length > 0 && (
              <div>
                <label className="form-label mb-2 block">
                  {t('members')} (optional)
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-[#e2e8f0] rounded-xl p-3">
                  {workers.map((worker) => (
                    <label key={worker.id} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-[#f8f9fb]">
                      <input
                        type="checkbox"
                        checked={newTeam.members.includes(worker.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewTeam((prev) => ({ ...prev, members: [...prev.members, worker.id] }));
                          } else {
                            setNewTeam(prev => ({
                              ...prev,
                              members: prev.members.filter(id => id !== worker.id)
                            }));
                          }
                        }}
                        className="w-4 h-4 rounded border-[#e2e8f0] text-[#00CBA9] focus:ring-[#00CBA9]"
                      />
                      <span className="text-sm">{worker.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowTeamDialog(false)}
                className="vmk-btn flex-1"
                data-testid="team-cancel-btn"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleCreateTeam}
                className="vmk-btn vmk-btn-primary flex-1"
                data-testid="team-save-btn"
              >
                {t('createTeam')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog
        open={showEditTeamDialog}
        onOpenChange={(open) => {
          setShowEditTeamDialog(open);
          if (!open) setEditTeam(null);
        }}
      >
        <DialogContent className="vmk-dialog-content max-w-md">
          <DialogHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-[#f8f9fb] dark:border-[#334155] dark:bg-[#111827]">
            <DialogTitle className="text-lg font-bold text-[#111827] dark:text-[#f8fafc]">{t('editTeam')}</DialogTitle>
            <DialogDescription className="sr-only">{t('editTeam')}</DialogDescription>
          </DialogHeader>
          {editTeam && (
            <div className="p-6 space-y-5 text-[#111827] dark:text-[#e2e8f0]">
              <div>
                <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">{t('teamName')}</label>
                <input
                  type="text"
                  value={editTeam.name}
                  onChange={(e) => setEditTeam((prev) => ({ ...prev, name: e.target.value }))}
                  className="vmk-input"
                />
              </div>
              <div>
                <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">{t('type')}</label>
                <Select
                  value={editTeam.type}
                  onValueChange={(value) =>
                    setEditTeam((prev) => ({
                      ...prev,
                      type: value,
                    }))
                  }
                >
                  <SelectTrigger className="vmk-input h-12 text-[#111827] dark:text-[#e2e8f0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#e2e8f0] text-[#111827] dark:bg-[#0f172a] dark:border-[#334155] dark:text-[#e2e8f0]">
                    <SelectItem className="text-[#111827] dark:text-[#e2e8f0]" value="individual">{t('individualWorker')}</SelectItem>
                    <SelectItem className="text-[#111827] dark:text-[#e2e8f0]" value="group">{t('teamGroup')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label mb-3 block text-[#334155] dark:text-[#cbd5e1]">{t('teamColor')}</label>
                <div className="flex gap-3 flex-wrap">
                  {TEAM_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setEditTeam((prev) => ({ ...prev, color }))}
                      className={`w-10 h-10 rounded-xl transition-all ${
                        editTeam.color === color ? 'ring-2 ring-offset-2 ring-[#1a1a1a] dark:ring-[#e2e8f0] dark:ring-offset-[#0f172a] scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              {workers.length > 0 && (
                <div>
                  <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">
                    {t('members')} (optional)
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-[#e2e8f0] dark:border-[#334155] rounded-xl p-3">
                    {workers.map((worker) => (
                      <label
                        key={worker.id}
                        className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-[#f8f9fb] dark:hover:bg-[#1e293b]"
                      >
                        <input
                          type="checkbox"
                          checked={editTeam.members.includes(worker.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditTeam((prev) => ({ ...prev, members: [...prev.members, worker.id] }));
                            } else {
                              setEditTeam((prev) => ({
                                ...prev,
                                members: prev.members.filter((id) => id !== worker.id),
                              }));
                            }
                          }}
                          className="w-4 h-4 rounded border-[#e2e8f0] text-[#00CBA9] focus:ring-[#00CBA9]"
                        />
                        <span className="text-sm text-[#111827] dark:text-[#e2e8f0]">{worker.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditTeamDialog(false)} className="vmk-btn flex-1 text-[#111827] dark:text-[#e2e8f0]">
                  {t('cancel')}
                </button>
                <button onClick={handleSaveEditTeam} className="vmk-btn vmk-btn-primary flex-1">
                  {t('saveChanges')}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Worker Dialog */}
      <Dialog open={showWorkerDialog} onOpenChange={setShowWorkerDialog}>
        <DialogContent className="vmk-dialog-content max-w-md">
          <DialogHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-[#f8f9fb]">
            <DialogTitle className="text-lg font-bold">{t('addNewWorker')}</DialogTitle>
            <DialogDescription className="sr-only">{t('addNewWorker')}</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-5">
            <div>
              <label className="form-label mb-2 block">{t('name')}</label>
              <input
                type="text"
                value={newWorker.name}
                onChange={(e) => setNewWorker(prev => ({ ...prev, name: e.target.value }))}
                className="vmk-input"
                placeholder={t('workerNamePlaceholder')}
                data-testid="worker-name-input"
              />
            </div>
            <div>
              <label className="form-label mb-2 block">{t('phone')}</label>
              <input
                type="tel"
                value={newWorker.phone}
                onChange={(e) => setNewWorker(prev => ({ ...prev, phone: e.target.value }))}
                className="vmk-input"
                placeholder={t('workerPhonePlaceholder')}
                data-testid="worker-phone-input"
              />
            </div>
            <div>
              <label className="form-label mb-2 block">{t('email')}</label>
              <input
                type="email"
                value={newWorker.email}
                onChange={(e) => setNewWorker(prev => ({ ...prev, email: e.target.value }))}
                className="vmk-input"
                placeholder={t('workerEmailPlaceholder')}
                data-testid="worker-email-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowWorkerDialog(false)}
                className="vmk-btn flex-1"
                data-testid="worker-cancel-btn"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleCreateWorker}
                className="vmk-btn vmk-btn-primary flex-1"
                data-testid="worker-save-btn"
              >
                {t('addWorker')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Worker Dialog */}
      <Dialog
        open={showEditWorkerDialog}
        onOpenChange={(open) => {
          setShowEditWorkerDialog(open);
          if (!open) setEditWorker(null);
        }}
      >
        <DialogContent className="vmk-dialog-content max-w-md dark:bg-[#0f172a] dark:border-[#334155]">
          <DialogHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-[#f8f9fb] dark:border-[#334155] dark:bg-[#111827]">
            <DialogTitle className="text-lg font-bold text-[#111827] dark:text-[#f8fafc]">{t('editWorker')}</DialogTitle>
            <DialogDescription className="sr-only">{t('editWorker')}</DialogDescription>
          </DialogHeader>
          {editWorker && (
            <div className="p-6 space-y-5 text-[#111827] dark:text-[#e2e8f0]">
              <div>
                <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">{t('name')}</label>
                <input
                  type="text"
                  value={editWorker.name}
                  onChange={(e) => setEditWorker((prev) => ({ ...prev, name: e.target.value }))}
                  className="vmk-input"
                />
              </div>
              <div>
                <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">{t('phone')}</label>
                <input
                  type="tel"
                  value={editWorker.phone}
                  onChange={(e) => setEditWorker((prev) => ({ ...prev, phone: e.target.value }))}
                  className="vmk-input"
                />
              </div>
              <div>
                <label className="form-label mb-2 block text-[#334155] dark:text-[#cbd5e1]">{t('email')}</label>
                <input
                  type="email"
                  value={editWorker.email}
                  onChange={(e) => setEditWorker((prev) => ({ ...prev, email: e.target.value }))}
                  className="vmk-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditWorkerDialog(false)} className="vmk-btn flex-1 text-[#111827] dark:text-[#e2e8f0]">
                  {t('cancel')}
                </button>
                <button onClick={handleSaveEditWorker} className="vmk-btn vmk-btn-primary flex-1">
                  {t('saveChanges')}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
